import { motion, useReducedMotion } from 'framer-motion'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import type { TokenUsageCopy, TokenUsageReport } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import UsageBreakdowns from './UsageBreakdowns'
import NextUsageUpdate from './NextUsageUpdate'
import UsageSummary from './UsageSummary'
import UsageTimeline from './UsageTimeline'

interface Props {
  workerId: string
  report: TokenUsageReport
  copy: TokenUsageCopy
  canManage: boolean
}

export default function TokenUsageAnalytics({ workerId, report, copy, canManage }: Props) {
  const reduceMotion = useReducedMotion()
  const reveal = reduceMotion
    ? undefined
    : {
        hidden: { opacity: 0, y: 6 },
        visible: { opacity: 1, y: 0 },
      }
  const hasPeriodData = report.has_data

  return (
    <motion.div
      className="space-y-4"
      style={agentHueStyle(workerId)}
      initial={reduceMotion ? false : 'hidden'}
      animate="visible"
      variants={reduceMotion ? undefined : { visible: { transition: { staggerChildren: 0.055 } } }}
    >
      <motion.header variants={reveal} transition={{ duration: 0.22, ease: 'easeOut' }}>
        <h1 className="text-[21px] font-semibold leading-tight tracking-[-0.015em] text-foreground">{copy.title}</h1>
      </motion.header>

      {report.status === 'empty' ? (
        <motion.section variants={reveal} transition={{ duration: 0.22, ease: 'easeOut' }} className="flex min-h-56 flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card px-6 text-center">
          <h2 className="text-[14px] font-semibold text-foreground">{copy.empty.title}</h2>
          <p className="mt-2 max-w-md text-[13px] leading-relaxed text-muted-foreground">{copy.empty.body}</p>
          <div className="mt-4">
            <NextUsageUpdate nextUpdateAt={report.next_update_at} label={copy.next_update} />
          </div>
        </motion.section>
      ) : (
        <>
          <motion.div variants={reveal} transition={{ duration: 0.22, ease: 'easeOut' }}><UsageSummary report={report} copy={copy} /></motion.div>
          <motion.section variants={reveal} transition={{ duration: 0.22, ease: 'easeOut' }} className="overflow-hidden rounded-xl border border-border bg-card shadow-xs">
            <UsageTimeline workerId={workerId} report={report} copy={copy} />
            {hasPeriodData ? (
              <UsageBreakdowns workerId={workerId} report={report} copy={copy} canManage={canManage} />
            ) : (
              <div className="flex min-h-36 flex-col items-center justify-center px-6 pb-5 text-center">
                <h2 className="text-[14px] font-semibold text-foreground">{copy.no_period_data.title}</h2>
                <p className="mt-2 max-w-md text-[13px] leading-relaxed text-muted-foreground">{copy.no_period_data.body}</p>
              </div>
            )}
          </motion.section>
        </>
      )}
    </motion.div>
  )
}
